// First, back, forward and Latest over the game's own moves (§29 U4).
//
// **In the turn row and in the watch transport's own group**, because it is
// the same control: four small buttons that drive which board you are looking
// at. A player holds a seat and steps through history; a spectator holds no
// seat and drives the clock — the two never draw together, so the slot is
// shared and the styling with it.
//
// The model is `historyTransport.ts` and is pure; this decides nothing except
// where a press lands and how long Latest flashes.

import { useCallback, useEffect, useRef, useState } from "react";

import { FLASH_MS, historyTransport } from "../historyTransport";
import { inField } from "../keyGuard";
import { HoverCard, Icon, keyframes, REDUCED_MOTION, STILL, styled } from "../kit";
import { OffBtn, Transport } from "../TurnRow";
import type { BoardInteraction } from "../useBoardInteraction";
import { useGame } from "../useGame";
import { useSessionView } from "../useSessionView";

/**
 * Latest, saying "here" when a click was refused for looking back.
 *
 * A ring and a swell rather than an opacity dip: the dip was invisible at
 * playtest — "the pill does appear but the 'latest' button does not signify
 * importance anywhere or anyhow at all" — because a button that only gets
 * *fainter* reads as a repaint. Growing and casting a brand ring is the one
 * thing on screen doing something.
 */
const FLASH = keyframes({
  "0%, 100%": { transform: "scale(1)", boxShadow: "var(--flash-drop)" },
  "20%, 60%": {
    transform: "scale(1.14)",
    boxShadow: "var(--flash-drop), 0 0 0 7px var(--flash-ring)"
  }
});

/**
 * Latest.
 *
 * **The animation is a class, not a `style` string.** Emotion's `keyframes`
 * hands back an object whose `toString()` is the animation *name*, and the
 * `@keyframes` rule itself is only written when that object is serialised
 * inside a `styled`/`css` block — so the first draft, which interpolated the
 * name into an inline `style`, named a rule that had never been injected and
 * animated nothing at all. That is the whole of why this exists.
 *
 * It does not break "anything that moves goes in `style`, never `sx`": that
 * rule is about emotion minting a class per *value*, and this is one fixed
 * keyframe behind an attribute selector — one class for the life of the app,
 * switched by `data-flash`. The reduced-motion guard is the kit's rule and is
 * not optional; the accent variant still says "way out" with it on.
 *
 * The two colours ride in as custom properties because a `keyframes` block is
 * minted at module scope and has no theme to ask — and a hex typed into a
 * `.tsx` outside the kit is the thing `kit.test.ts` forbids.
 */
const LatestBtn = styled(OffBtn)(({ theme }) => ({
  // The drop `Btn` already casts, restated so the ring can be added to it
  // rather than replacing it — a shadow property is one value, not a list of
  // layers something else can append to.
  "--flash-drop": `0 2.5px 0 ${theme.kit.color.shadow}`,
  "--flash-ring": `color-mix(in srgb, ${theme.kit.color.brand} 60%, transparent)`,
  '&[data-flash="true"]': { animation: `${FLASH} ${FLASH_MS}ms ease-out` },
  [REDUCED_MOTION]: { '&[data-flash="true"]': STILL }
}));

/**
 * How far behind Latest you are, trailing its icon the way `Moves` does.
 *
 * `inherit` and not `inkDim`: while there is a view to leave Latest is drawn
 * in brand, and a dim grey numeral on it is unreadable. Dimmer than the icon
 * by opacity, which works on either fill.
 */
const Behind = styled("span")(({ theme }) => ({
  marginLeft: 4,
  fontFamily: theme.kit.font.body,
  fontWeight: 700,
  fontSize: 12,
  color: "inherit",
  opacity: 0.78
}));

export function HistoryTransportZone(_props: {
  /** @deprecated Unread since day 8 — the zone reads the session's view. */
  board_?: BoardInteraction;
}) {
  const game = useGame();
  const session = useSessionView();
  // Read on the session's revision, which moves with every store change: the
  // view is the game's own `history()`, and subscribing here is what keeps the
  // four buttons current.
  const view = game.board.history();
  const [flashing, setFlashing] = useState(false);

  /**
   * **The pill's own counter is the bell**: every refusal said while looking
   * back bumps it — including a second refusal saying the same sentence, since
   * the engine counts rather than compares — so nothing that refuses has to
   * remember to ring Latest as well, and a second click flashes again. Zero is
   * "nothing has been refused", which must not restart the timer on every
   * unrelated render. Every click on a viewed board is refused for looking
   * back, which is why the mode is the test rather than the sentence.
   */
  const flash =
    session.mode === "viewing" && session.refusal.text !== null ? session.refusal.said : 0;
  useEffect(() => {
    if (flash === 0) return undefined;
    // Off and on again, or a second refusal inside the first flash restarts
    // nothing: the attribute never left, so the animation never re-ran.
    setFlashing(false);
    const start = window.setTimeout(() => setFlashing(true), 0);
    const timer = window.setTimeout(() => setFlashing(false), FLASH_MS);
    return () => {
      window.clearTimeout(start);
      window.clearTimeout(timer);
    };
  }, [flash]);

  const model = historyTransport({
    count: view.count,
    view: view.step,
    ahead: view.ahead,
    flashing
  });

  /**
   * Land on a board. The engine takes the selection with the step: what is
   * selected was picked on a different position, and its legal moves are
   * drawn for one the player can no longer see.
   */
  const go = useCallback((to: number | null) => game.play({ kind: "history", step: to }), [game]);

  /**
   * Left and right mirror Back and Forward.
   *
   * Read through a ref so the listener is attached once: `model` is rebuilt
   * every render, so depending on it would add and remove a window listener
   * on every frame the camera moves.
   */
  const live = useRef({ model, go });
  live.current = { model, go };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // Never out of a field: the room code, the feedback box and the dev
      // menu all take typing, and a caret moving is what those keys mean there.
      // Shared with the camera's keys — two copies of this test drift, and the
      // second one written is the one that forgets `contentEditable`.
      if (inField(event)) return;
      const want = event.key === "ArrowLeft" ? "history-back" : "history-forward";
      const button = live.current.model.buttons.find((one) => one.id === want)!;
      if (!button.enabled) return;
      event.preventDefault();
      live.current.go(button.to);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <Transport data-testid="history-transport">
      {model.buttons.map((one) => {
        const latest = one.id === "history-latest";
        // Latest trailing a "+N" is the one button that cannot be a square.
        const counted = latest && model.behind !== null;
        // Latest is the accent button for as long as there is a view to leave,
        // not only while it flashes: the standing fact is "this is not the
        // game", and a player who clicks nothing still has to see the door.
        const Button = latest ? LatestBtn : OffBtn;
        return (
          <HoverCard key={one.id} placement="bottom" title={one.label} card={one.card}>
            <Button
              small
              // Icon-only, so the box is square and the padding even — the
              // kit's own form, never a wrapper minted here.
              icon={!counted}
              variant={latest && model.accent ? "primary" : "default"}
              data-testid={one.id}
              aria-disabled={!one.enabled}
              // For the smoke, which cannot see an animation, and the selector
              // the keyframe hangs off.
              data-flash={latest && model.flash ? "true" : undefined}
              onClick={() => {
                if (one.enabled) go(one.to);
              }}
            >
              {/* The button's only name — there is no word beside it now. */}
              <Icon name={one.icon} size={16} label={one.label} />
              {counted && <Behind data-testid="history-behind">{model.behind}</Behind>}
            </Button>
          </HoverCard>
        );
      })}
    </Transport>
  );
}
